import { useState } from 'react';
import { Bot, Loader2 } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import { Input } from '@/app/components/ui/input';
import { Textarea } from '@/app/components/ui/textarea';
import { useCreateCharacter, type CharacterCategory } from '@/api';

const CATEGORIES: CharacterCategory[] = [
  'screen-legends',
  'toons',
  'irl-vibes',
  'memeverse',
  'original-chaos',
];

const CATEGORY_LABELS: Record<CharacterCategory, string> = {
  'screen-legends': 'Screen Legends',
  'toons': 'Toons',
  'irl-vibes': 'IRL Vibes',
  'memeverse': 'Memeverse',
  'original-chaos': 'Original Chaos',
};

export function AdminCharactersPage() {
  const [form, setForm] = useState({
    name: '',
    personality: '',
    tags: '',
    systemPrompt: '',
    category: 'screen-legends' as CharacterCategory,
  });

  const createCharacter = useCreateCharacter();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await createCharacter.mutateAsync({
        name: form.name,
        description: form.personality,
        system_prompt: { prompt: form.systemPrompt },
        persona_json: form.tags
          ? { tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean) }
          : {},
        category: form.category,
      });
      setForm({ name: '', personality: '', tags: '', systemPrompt: '', category: 'screen-legends' });
    } catch (err) {
      console.error('Create failed:', err);
    }
  };

  return (
    <div>
      <h1 className="font-['Poppins',sans-serif] text-4xl font-semibold text-white mb-3">
        Create Character
      </h1>
      <p className="text-lg text-[#9595a5] mb-10">Add a new AI character to the platform</p>

      <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
        <div className="p-6 rounded-xl bg-[#18181f] border border-white/[0.08] space-y-6">
          <div>
            <label className="block text-sm font-medium text-white mb-2">Character Name</label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g., AI Assistant Pro"
              className="bg-[#2a2a35] border-white/[0.08] text-white placeholder-[#9595a5] focus:border-white/30"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-white mb-2">Personality / Description</label>
            <Input
              value={form.personality}
              onChange={(e) => setForm({ ...form, personality: e.target.value })}
              placeholder="e.g., Helpful and knowledgeable"
              className="bg-[#2a2a35] border-white/[0.08] text-white placeholder-[#9595a5] focus:border-white/30"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-white mb-2">Category</label>
            <select
              value={form.category}
              onChange={(e) => setForm({ ...form, category: e.target.value as CharacterCategory })}
              className="w-full bg-[#2a2a35] border border-white/[0.08] rounded-xl px-4 py-3 text-white focus:border-white/30 focus:outline-none focus:ring-2 focus:ring-white/20"
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>{CATEGORY_LABELS[c]}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-white mb-2">Tags (comma-separated)</label>
            <Input
              value={form.tags}
              onChange={(e) => setForm({ ...form, tags: e.target.value })}
              placeholder="General, Productivity, AI"
              className="bg-[#2a2a35] border-white/[0.08] text-white placeholder-[#9595a5] focus:border-white/30"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-white mb-2">System Prompt</label>
            <Textarea
              value={form.systemPrompt}
              onChange={(e) => setForm({ ...form, systemPrompt: e.target.value })}
              placeholder="Define the character's behavior..."
              className="bg-[#2a2a35] border-white/[0.08] text-white placeholder-[#9595a5] min-h-[150px] focus:border-white/30"
              required
            />
          </div>
        </div>

        <Button
          type="submit"
          disabled={createCharacter.isPending}
          className="bg-white text-black hover:opacity-90 font-medium py-6 rounded-xl"
        >
          {createCharacter.isPending ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <>
              <Bot className="w-5 h-5 mr-2" />
              Create Character
            </>
          )}
        </Button>
        {createCharacter.isError && (
          <p className="text-sm text-red-400">{createCharacter.error.message}</p>
        )}
        {createCharacter.isSuccess && (
          <p className="text-sm text-green-400">Character created</p>
        )}
      </form>
    </div>
  );
}
